import { useEffect, useMemo } from 'react'
import { useBlogCms } from '@/hooks/useBlogCms'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { BLOG_CONTENT_API, SITE_URL } from '@/constants'
import { replaceString } from '@/utils/replaceString'
import type { NextPage, GetServerSidePropsContext } from 'next'
import {
  Box,
  Center,
  Flex,
  HStack,
  Link,
  Image,
  Progress,
  Text,
  VStack,
  useStyleConfig,
} from '@chakra-ui/react'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { Pagination } from '@/components/Pagination'
import { Article } from '@/features/education/article'

const AuthorPage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const t = useTranslations()
  const { compassContent, loading, getContent } = useBlogCms()
  const {
    compassContent: authors,
    loading: loadingAuthor,
    getContent: getAuthor,
  } = useBlogCms()
  const router = useRouter()

  useEffect(() => {
    if (!router?.query?.slug) return

    const abortController = new AbortController()
    getContent(
      {
        path: `/content/posts`,
        params: `&filter=author:${router?.query?.slug}&page=1&limit=24&include=tags,authors&order=published_at%20desc`,
      },
      abortController
    )

    const abortController2 = new AbortController()
    getAuthor(
      {
        path: `/content/authors/slug/${router?.query?.slug}`,
        params: '',
      },
      abortController2
    )

    return () => {
      abortController.abort()
      abortController2.abort()
    }
  }, [router])

  const pagingHandler = (paging: any) => {
    const abortController = new AbortController()

    const limit = compassContent?.meta?.pagination?.limit
    let params = ''
    if (paging?.firstPage) {
      params = `&page=1&limit=24`
    } else if (paging?.lastPage) {
      params = `&page=${compassContent?.meta?.pagination?.pages}&limit=${limit}`
    } else if (paging?.next) {
      params = `&page=${compassContent?.meta?.pagination?.next}&limit=${limit}`
    } else if (paging?.prev) {
      params = `&page=${compassContent?.meta?.pagination?.prev}&limit=${limit}`
    } else if (paging?.changeLimit) {
      params = `&page=1&limit=${paging?.pageLimit}`
    } else {
      params = `&page=${compassContent?.meta?.pagination?.next}&limit=${limit}`
    }

    return getContent(
      {
        path: '/content/posts',
        params: `${params}&filter=author:${router?.query?.slug}&include=tags,authors&order=published_at%20desc`,
      },
      abortController
    )
  }

  const author = authors?.authors?.length > 0 ? authors?.authors[0] : null

  return (
    <>
      <Box __css={styles}>
        <ContainerBase py="50px" maxWidth={{ base: '100%', xl: '1200px' }}>
          {(loading || loadingAuthor) && (
            <Progress size="xs" isIndeterminate colorScheme="brand" />
          )}
          {author && (
            <Flex
              flexDirection={{ base: 'column', md: 'row' }}
              alignItems="center"
              gridGap={{ base: '20px', md: '30px' }}
            >
              <Box borderRadius="full" width="110px" height="110px">
                <Image
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  borderRadius="full"
                  src={author?.profile_image}
                />
              </Box>
              <VStack alignItems={{ base: 'center', md: 'flex-start' }}>
                <Box
                  fontWeight="600"
                  fontSize={{ base: '42px', md: '50px' }}
                  lineHeight="normal"
                >
                  {author?.name}
                </Box>
                <Box
                  fontSize={{ base: '18px', md: '20px' }}
                  color="var(--midgrey)"
                  lineHeight="normal"
                >
                  {author?.bio}
                </Box>
                <HStack fontSize="12px" spacing="10px">
                  <Text color="var(--midgrey)">
                    {compassContent?.meta?.pagination?.total} POSTS
                  </Text>
                  {author?.website && (
                    <>
                      <Box color="var(--midgrey)">•</Box>
                      <Link
                        href={author?.website}
                        isExternal
                        color="var(--text-secondary)"
                        fontWeight="bold"
                      >
                        WEBSITE
                      </Link>
                    </>
                  )}
                  {author?.twitter && (
                    <>
                      <Box color="var(--midgrey)">•</Box>
                      <Link
                        href={`https://x.com/${(author?.twitter)}`}
                        isExternal
                        color="var(--text-secondary)"
                        fontWeight="bold"
                      >
                        X
                      </Link>
                    </>
                  )}
                </HStack>
              </VStack>
            </Flex>
          )}
          <Flex flexWrap="wrap" paddingTop="100px">
            {compassContent?.posts?.map((article: any) => {
              return <Article key={article?.id} article={article} />
            })}
          </Flex>
          <Center>
            {useMemo(
              () =>
                compassContent?.meta?.pagination && (
                  <Pagination
                    perPageLabel={`Articles per page`}
                    pageOptions={['6', '9', '24', '60']}
                    pageLimit={compassContent?.meta?.pagination?.limit}
                    refetch={null}
                    pagingHandler={pagingHandler}
                    totalCount={compassContent?.meta?.pagination?.total}
                    pageInfo={{
                      endCursor: '',
                      hasNextPage: !!compassContent?.meta?.pagination?.next,
                      hasPreviousPage: !!compassContent?.meta?.pagination?.prev,
                      startCursor: '',
                      __typename: '',
                    }}
                  />
                ),
              [compassContent?.meta?.pagination]
            )}
          </Center>
        </ContainerBase>
      </Box>
    </>
  )
}

export async function getServerSideProps({
  locale,
  params,
}: GetServerSidePropsContext) {
  let author: any = {}
  try {
    const resource = (path: string) => {
      return replaceString(BLOG_CONTENT_API, {
        key: 'resource',
        value: path,
      })
    }

    const path = `/content/authors/slug/${params?.slug}`
    const res = await fetch(`${resource(path)}`, {})
    const authors = await res.json()
    author = authors?.authors?.length > 0 ? authors?.authors[0] : null

    if (!res.ok) {
      if (res.status === 404) {
        return { notFound: true }
      }
    }
  } catch (e) {
    console.error(JSON.stringify(e))
  }

  return {
    props: {
      messages: (await import(`../../../../locales/${locale}.json`)).default,
      revalidate: 60,
      htmlClass: 'education',
      seo: {
        title: `${author?.name} - Compass Mining Content`,
        description: author?.bio || '',
        image: author?.profile_image || '',
        url: `${SITE_URL}/education/author/${params?.slug}`,
        breadcrumbs: [
          {
            position: 2,
            item: `${SITE_URL}/education`,
            name: 'Compass Mining Content',
          },
          {
            position: 3,
            item: `${SITE_URL}/education/author/${params?.slug}`,
            name: author?.name || '',
          },
        ],
      },
    },
  }
}

export default AuthorPage
